// Modal del builder admin: edita un visual exponiendo su CÓDIGO en dos capas —
// cómo se construye la gráfica + cómo se jalan los datos (SQL) — con preview en
// vivo y "Copiar para Claude". Dos modos para la gráfica:
//  - Declarativo: render_config (JSON) → builders config-driven.
//  - Código: la FUNCIÓN real (data, theme, helpers) => option, editable como el
//    código que tocamos a mano (hiperpersonalización a nivel componente).
import React, { useMemo, useState, useEffect } from 'react';
import VisualEditorTabs from './VisualEditorTabs';
import VisualRenderer from '../../../visuals/VisualRenderer';
import { mapWidgetToBlock } from '../../../visuals/configs/mapDashboardToConfig';
import { compileChartFunction } from '../../../visuals/echarts/customRuntime';
import { getTemplate, TEMPLATE_KINDS, TEMPLATE_FLAVORS } from './builderTemplates';

const WIDGET_TYPES = [
  { id: 'echarts', label: 'Gráfica ECharts' },
  { id: 'kpi', label: 'KPI' },
  { id: 'kpiPair', label: 'Par de KPIs' },
];

// Plantillas del modo DECLARATIVO (para no partir de un {} en blanco).
const DECLARATIVE_TEMPLATES = {
  echarts: {
    source: 'timeseries', chart: 'line', title: 'Nueva gráfica',
    encoding: { x: 'date', y: 'priceIndex', format: 'pct', targetValue: -10 },
    option: {},
  },
  kpi: {
    metric: 'priceIndexGeneral', label: 'Nuevo KPI', sublabel: 'Last Update',
    format: 'pct', colorBySign: true,
  },
  kpiPair: {
    items: [
      { metric: 'priceIndexNegative', label: 'Price Index', sublabel: 'Negativos', format: 'pct', tone: 'negative' },
      { metric: 'productsNegative', label: '# Productos', sublabel: 'Negativos', format: 'milesK', tone: 'negative' },
    ],
  },
};

const CHART_KINDS = ['multiLine', 'line', 'heatmap', 'gauge'];

export default function VisualWidgetEditorModal({ open, mode, initial, theme, data, onSubmit, onClose, busy, onRunSql }) {
  const [title, setTitle] = useState('');
  const [widgetType, setWidgetType] = useState('echarts');
  const [size, setSize] = useState(12);
  const [editorMode, setEditorMode] = useState('declarative'); // 'declarative' | 'custom'
  const [renderConfigText, setRenderConfigText] = useState('{}');
  const [codeText, setCodeText] = useState('');
  const [dataSql, setDataSql] = useState('');
  const [tplKind, setTplKind] = useState('multiLine');
  const [tplFlavor, setTplFlavor] = useState('full');
  const [sqlRows, setSqlRows] = useState(null);   // resultado de "Probar SQL" (alimenta el preview)
  const [sqlBusy, setSqlBusy] = useState(false);
  const [sqlMsg, setSqlMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    const initialType = initial?.widget_type || 'echarts';
    const rc = initial?.render_config || {};
    const wasCustom = rc.mode === 'custom';
    setTitle(initial?.title || '');
    setWidgetType(initialType);
    setSize(initial?.size || 12);
    setEditorMode(wasCustom ? 'custom' : 'declarative');
    setRenderConfigText(JSON.stringify(wasCustom ? {} : (initial?.render_config || DECLARATIVE_TEMPLATES[initialType] || {}), null, 2));
    setCodeText(wasCustom ? (rc.code || '') : '');
    setTplKind(CHART_KINDS.includes(rc.chart) ? rc.chart : 'multiLine');
    setDataSql(initial?.data_sql || '');
    setSqlRows(null);
    setSqlMsg(null);
  }, [open, initial]);

  // El modo Código solo aplica a widgets ECharts.
  const isCustom = editorMode === 'custom' && widgetType === 'echarts';

  // Modo declarativo: parse + validación del JSON.
  const { config: parsedConfig, error: jsonError } = useMemo(() => {
    try { return { config: JSON.parse(renderConfigText), error: null }; }
    catch (e) { return { config: null, error: e.message }; }
  }, [renderConfigText]);

  // Modo código: compile-check (no ejecuta, solo valida que compile a función).
  const codeError = useMemo(() => {
    if (!isCustom) return null;
    return compileChartFunction(codeText).error;
  }, [isCustom, codeText]);

  // PREVIEW en vivo con el mismo `data` del endpoint.
  const previewBlock = useMemo(() => {
    const widgetData = sqlRows ? { rows: sqlRows } : undefined; // resultado de "Probar SQL"
    if (isCustom) {
      if (codeError) return null;
      return {
        type: 'echarts', span: size, mode: 'custom', code: codeText,
        chart: tplKind, title, _widgetId: initial?.id || 'preview', _widgetData: widgetData,
      };
    }
    if (!parsedConfig) return null;
    const block = mapWidgetToBlock({ id: initial?.id || 'preview', widget_type: widgetType, title, size, render_config: parsedConfig });
    if (widgetData) block._widgetData = widgetData;
    return block;
  }, [isCustom, codeError, codeText, tplKind, parsedConfig, widgetType, title, size, initial, sqlRows]);

  const insertTemplate = () => {
    // No pisar trabajo en curso sin confirmar.
    if (codeText.trim() && !window.confirm('¿Reemplazar el código actual con la plantilla?')) return;
    setCodeText(getTemplate(tplKind, tplFlavor, {
      source: initial?.render_config?.source,
      metric: initial?.render_config?.encoding?.metric,
    }));
  };

  // "Probar SQL": ejecuta el data_sql (sin guardar) y alimenta el preview con las filas.
  const handleRunSql = async () => {
    if (!onRunSql || !dataSql.trim() || sqlBusy) return;
    setSqlBusy(true); setSqlMsg(null);
    try {
      const res = await onRunSql(dataSql);
      const rows = res?.rows || [];
      setSqlRows(rows);
      const warn = res?.warning ? ` — ${res.warning}` : '';
      setSqlMsg({ ok: true, text: `✓ ${res?.rowCount ?? rows.length} filas en ${res?.computeMs ?? '?'} ms${warn}` });
    } catch (e) {
      setSqlRows(null);
      setSqlMsg({ ok: false, text: e.message || 'Error al ejecutar el SQL' });
    } finally {
      setSqlBusy(false);
    }
  };

  const useDeclarativeTemplate = () => {
    setRenderConfigText(JSON.stringify(DECLARATIVE_TEMPLATES[widgetType] || {}, null, 2));
  };

  const changeEditorMode = (m) => {
    setEditorMode(m);
    // Al entrar a Código sin código, arrancar con una plantilla para no ver vacío.
    if (m === 'custom' && !codeText.trim()) {
      setCodeText(getTemplate(tplKind, tplFlavor, {
        source: initial?.render_config?.source,
        metric: initial?.render_config?.encoding?.metric,
      }));
    }
  };

  const copyForClaude = () => {
    const dataKeys = Object.keys(data || {}).join(', ') || '(sin datos cargados)';
    const text = isCustom
      ? `# Visual: ${title || '(sin título)'} [modo=Código, size=${size}]

## Función render (JS) — contrato: (data, theme, helpers) => echartsOption
helpers = { buildLineChartWithMarkLine, buildMultiLineChart, buildHeatmapMatrix, buildKPIGauge, generateThemeColors, axisFormatter, deepMerge, formatValue, theme }
\`\`\`js
${codeText}
\`\`\`

## data_sql (Athena)
\`\`\`sql
${dataSql || '-- (vacío)'}
\`\`\`

## Claves disponibles en "data" (payload del dashboard)
${dataKeys}
`
      : `# Visual: ${title || '(sin título)'} [type=${widgetType}, size=${size}]

## render_config (JSON) — la option base se arma de source+chart+encoding; "option" se DEEP-MERGEA encima
\`\`\`json
${renderConfigText}
\`\`\`

## data_sql (Athena)
\`\`\`sql
${dataSql || '-- (vacío)'}
\`\`\`

## Claves disponibles en "data"
${dataKeys}
`;
    if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text);
  };

  const submitDisabled = busy || !title.trim() || (isCustom ? (!!codeError || !codeText.trim()) : !!jsonError);

  const handleSubmit = () => {
    if (submitDisabled) return;
    const render_config = isCustom
      ? { mode: 'custom', code: codeText, chart: tplKind }
      : parsedConfig;
    onSubmit?.({ widget_type: widgetType, title: title.trim(), size: Number(size), render_config, data_sql: dataSql });
  };

  if (!open) return null;
  return (
    <div className="vz-modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="vz-modal" onClick={(e) => e.stopPropagation()}>
        <header className="vz-modal-head">
          <h3>{mode === 'edit' ? 'Editar visual' : 'Nuevo visual'}</h3>
          <button className="vz-modal-x" onClick={onClose} disabled={busy}>×</button>
        </header>

        <div className="vz-modal-row">
          <label>Título
            <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>Tipo
            <select value={widgetType} onChange={(e) => {
              const t = e.target.value;
              setWidgetType(t);
              // El modo Código solo aplica a echarts: al cambiar de tipo, lo reseteamos.
              if (t !== 'echarts') setEditorMode('declarative');
            }}>
              {WIDGET_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          {widgetType === 'echarts' && (
            <label>Modo
              <select value={editorMode} onChange={(e) => changeEditorMode(e.target.value)}>
                <option value="declarative">Declarativo</option>
                <option value="custom">Código</option>
              </select>
            </label>
          )}
          <label>Tamaño
            <select value={size} onChange={(e) => setSize(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}/12</option>)}
            </select>
          </label>
        </div>

        {isCustom && (
          <div className="vz-admin-banner">
            ⚠️ Modo Código: la función corre en el navegador con los permisos de la app. Solo para admins — no pegues código que no revisaste.
          </div>
        )}

        <VisualEditorTabs
          editorMode={isCustom ? 'custom' : 'declarative'}
          renderConfig={renderConfigText}
          onRenderConfigChange={setRenderConfigText}
          jsonError={jsonError}
          code={codeText}
          onCodeChange={setCodeText}
          codeError={codeError}
          dataSql={dataSql}
          onDataSqlChange={setDataSql}
          sqlToolbar={
            <div className="vz-tpl-controls">
              <button className="vz-btn-link" type="button" onClick={handleRunSql} disabled={sqlBusy || !dataSql.trim()}>
                {sqlBusy ? 'Ejecutando…' : '▶ Probar SQL'}
              </button>
              {sqlMsg && <span className={sqlMsg.ok ? 'vz-hint' : 'vz-error-inline'}>{sqlMsg.text}</span>}
            </div>
          }
          toolbar={
            isCustom ? (
              <div className="vz-tpl-controls">
                <select value={tplKind} onChange={(e) => setTplKind(e.target.value)}>
                  {TEMPLATE_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
                <select value={tplFlavor} onChange={(e) => setTplFlavor(e.target.value)}>
                  {TEMPLATE_FLAVORS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <button className="vz-btn-link" type="button" onClick={insertTemplate}>Insertar plantilla</button>
              </div>
            ) : (
              <button className="vz-btn-link" type="button" onClick={useDeclarativeTemplate}>
                Usar plantilla {widgetType}
              </button>
            )
          }
          preview={
            previewBlock
              ? <div className="vz-preview-grid">
                  <VisualRenderer config={{ theme, layout: [previewBlock] }} data={data || {}} />
                </div>
              : <div className="vz-error">
                  {isCustom ? 'Corrige el código para ver el preview.' : 'JSON inválido — corrige el render_config para ver el preview.'}
                </div>
          }
        />

        <footer className="vz-modal-foot">
          <button className="vz-btn-secondary" onClick={copyForClaude} type="button">📋 Copiar para Claude</button>
          <span style={{ flex: 1 }} />
          <button className="vz-btn-secondary" onClick={onClose} disabled={busy} type="button">Cancelar</button>
          <button className="vz-btn-primary" onClick={handleSubmit} disabled={submitDisabled} type="button">
            {busy ? 'Guardando…' : mode === 'edit' ? 'Guardar cambios' : 'Crear visual'}
          </button>
        </footer>
      </div>
    </div>
  );
}
